import { useCallback } from "react";

import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";

import {
  useRedeemPendingLicenceQuery,
  useUpdatePendingLicenceCurrencyMutation,
} from "@evr/apis/pendingLicencePurchase";
import CurrencySelect from "@evr/components/CurrencySelect";
import { LicenceType, PaymentType } from "@evr/constant";
import { AssignTrialLicence, PurchaseSoftwareLicence } from "@evr/services/sso";
import { PurchaseLicenceRequest, SoftwareLicence } from "@evr/types";
import { ColumnContainer, RowContainer } from "@evr/ui/FlexBox";
import { LoadingBox } from "@evr/ui/LoadingBox";
import { Typography } from "@evr/ui/Typography";

import LicenceCard from "./LicenceCard";

const LicenceRow = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  margin-top: 1rem;
  border-top: 1px solid #ccc;
  border-bottom: 1px solid #ccc;
  height: 90%;
  justify-content: center;
  gap: 1rem;
`;

const CurrencyContainer = styled(ColumnContainer)`
  .select-with-label-group {
    margin-bottom: 0 !important;
  }
`;

const StyledColumnContainer = styled(ColumnContainer)`
  overflow: auto;
`;

const StyledLink = styled.a`
  color: blue;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    cursor: pointer;
    text-decoration: underline;
  }
`;

export const PurchaseLicence = () => {
  const location = useLocation();
  const state = location.state as any;
  const pendingLicencePurchaseId = state.licenceSummary?.id;
  const hash = state?.licenceSummary?.hash;
  const licenceName = state?.licenceSummary?.licenceName;

  const navigate = useNavigate();

  const onSubscribe = useCallback(
    (licence: SoftwareLicence) => {
      if (licence.licenceType === LicenceType.Trial) {
        AssignTrialLicence(pendingLicencePurchaseId, hash, licence.softwareLicenceId).then(r => {
          navigate("/sso?token=" + r.data.token + "&refreshToken=" + r.data.refreshToken);
        });
      } else {
        var request = {
          softwareLicenceId: licence.softwareLicenceId,
          pendingLicencePurchaseId: pendingLicencePurchaseId,
          hash: hash,
        } as PurchaseLicenceRequest;

        PurchaseSoftwareLicence(request).then(r => {
          window.location.href = r.data;
        });
      }
    },
    [pendingLicencePurchaseId, hash],
  );

  const { data, error, isLoading } = useRedeemPendingLicenceQuery({
    pendingLicencePurchaseId,
    hash,
  });

  const filteredLicences = data?.licences
    .filter(licence => licenceName === undefined || (licence.name !== licenceName && licence.name !== "Starter"))
    .filter(x => x.licenceType === LicenceType.SingleUser || x.licenceType === LicenceType.Trial)
    .filter(l => l.paymentType === PaymentType.RecurringPayment || l.paymentType === PaymentType.Free)
    .sort((a, b) => {
      const aRules = a.pricingRules;
      const bRules = b.pricingRules;

      const aHasRules = Array.isArray(aRules) && aRules.length > 0;
      const bHasRules = Array.isArray(bRules) && bRules.length > 0;

      if (!aHasRules && !bHasRules) {
        return 0;
      }
      if (!aHasRules) {
        return -1;
      }
      if (!bHasRules) {
        return 1;
      }

      return aRules[0].transactionCostIncTax - bRules[0].transactionCostIncTax;
    });

  const [updatePendingLicenceCurrency, { isLoading: isUpdatingCurrency }] = useUpdatePendingLicenceCurrencyMutation();

  const changePaymentCurrency = useCallback(
    async (currencyCode: string) => {
      try {
        await updatePendingLicenceCurrency({
          pendingLicencePurchaseId,
          hash,
          transactionCurrencyCode: currencyCode,
        }).unwrap();
      } catch (err) {}
    },
    [updatePendingLicenceCurrency, pendingLicencePurchaseId, hash],
  );

  if (error || (!isLoading && !data && (!filteredLicences || filteredLicences.length === 0))) {
    return (
      <ColumnContainer
        align="flex-start"
        justify="flex-start"
        flexGrow={1}
        overflow="hidden"
        position="relative"
        padding="5%"
      >
        <Typography fontWeight={500} size={1.7}>
          Error
        </Typography>
        <Typography fontWeight={100} size={1.2}>
          Error loading licences
        </Typography>
      </ColumnContainer>
    );
  }

  if (isLoading) {
    return <LoadingBox>Loading...</LoadingBox>;
  }

  return (
    <StyledColumnContainer
      align="flex-start"
      justify="flex-start"
      flexGrow={1}
      overflow="hidden"
      position="relative"
      padding="2% 5% 5% 5%"
      gap=".5rem"
    >
      <Typography fontWeight={500} size={1.7}>
        Available Plans
      </Typography>
      <Typography fontWeight={100} size={1.2}>
        There are {filteredLicences?.length ?? 0} available plans for you to choose from. Please select the one that
        best suits your needs.
      </Typography>
      <Typography fontWeight={100} size={1.2}>
        If you don't see the plan you want, please <StyledLink href="mailto:EVR@basemap.co.uk">contact us</StyledLink>
      </Typography>
      <RowContainer margin="1rem 0 0 0" justify="flex-start" width="100%">
        <ColumnContainer align="flex-start" maxWidth="25%">
          <Typography fontWeight={100} size={1.2}>
            Displaying prices in:
          </Typography>
        </ColumnContainer>
        <CurrencyContainer marginBottom="0" align="flex-end" width="25%">
          <CurrencySelect
            onCurrencyChange={changePaymentCurrency}
            currencyCode={data?.transactionCurrency?.code ?? "USD"}
          />
        </CurrencyContainer>
      </RowContainer>

      <LicenceRow>
        {isLoading && <Typography>Loading...</Typography>}
        {data &&
          filteredLicences?.map(licence => (
            <LicenceCard
              loading={isUpdatingCurrency}
              onSubscribe={onSubscribe}
              key={licence.softwareLicenceId}
              licence={licence}
              transactionCurrency={data.transactionCurrency}
            />
          ))}
      </LicenceRow>
    </StyledColumnContainer>
  );
};
